/*!
=========================================================
* Material Dashboard React - v1.9.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2020 Creative Tim
* Licensed under MIT
=========================================================
*/
import * as Sentry from '@sentry/react';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import {
  API_BASE_URL,
  APP_VERSION,
  CLAIM_CLIENT_URL,
  DATADOG_APPLICATION_ID,
  DATADOG_CLIENT_TOKEN,
  DATADOG_PRIVACY_LEVEL,
  DATADOG_SITE,
  INACTIVITY_LIMIT,
  INACTIVITY_POPUP_DURATION,
  POSTHOG_HOST,
  POSTHOG_KEY,
  SENTRY_DSN,
  datadogRumEnabled,
  isDev,
  isUat,
  isProduction,
  isProductionTest,
} from './config';

if (isDev || isUat || isProduction || isProductionTest) {
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.2,
    sendDefaultPii: false,
    environment: import.meta.env.VITE_STAGE,
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api\.local\.dev/,
      /^https:\/\/clientpaydirect\.com/,
    ],
  });
}

if (datadogRumEnabled) {
  datadogRum.init({
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: DATADOG_SITE,
    service: 'admin-portal-legacy',
    env: import.meta.env.VITE_STAGE,
    version: APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    sessionPersistence: 'local-storage',
    storeContextsAcrossPages: true,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: DATADOG_PRIVACY_LEVEL,
    plugins: [reactPlugin({ router: true })],
    enableExperimentalFeatures: ['feature_flags'],
    allowedTracingUrls: [API_BASE_URL],
  });
}

import ReactDOM from 'react-dom/client';
import { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes as ReactRouterRoutes,
  useNavigate,
} from 'react-router-dom';
import { Routes as DatadogRoutes } from '@datadog/browser-rum-react/react-router-v7';

import axios from 'axios';
import moment from 'moment';
import { pdfjs } from 'react-pdf';
import { Bounce, toast, ToastContainer } from 'react-toastify';
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';
import './translations/config';
import i18n from './translations/config';
import { isValidationError } from './utils/validationErrors';

import {
  adaptV4Theme,
  createTheme,
  StyledEngineProvider,
  ThemeProvider,
} from '@mui/material';

import './assets/css/material-dashboard-react.css?v=1.9.0';
import './assets/scss/global.scss';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import InactivityModal from './components/InactivityModal/InactivityModal';
import NeedHelp from './components/NeedHelp/NeedHelp';
import Redirect from './components/Redirect/Redirect';
import Layout from './layouts/Layout';
import User from './layouts/User';
import { routes } from './routes';
import { facilityRoutes } from './facility.routes';
import { isPathOpen } from './utility';
import { ConfirmationProvider } from './context/ConfirmationContext';

const Routes = datadogRumEnabled ? DatadogRoutes : ReactRouterRoutes;

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

axios.defaults.withCredentials = true;

posthog.init(POSTHOG_KEY, {
  api_host: POSTHOG_HOST,
  ui_host: 'https://us.posthog.com',
});

const originalIsFeatureEnabled = posthog.isFeatureEnabled.bind(posthog);
posthog.isFeatureEnabled = (key, options) => {
  const value = originalIsFeatureEnabled(key, options);
  if (datadogRumEnabled && value !== undefined) {
    datadogRum.addFeatureFlagEvaluation(key, value);
  }
  return value;
};

const theme = createTheme(
  adaptV4Theme({
    palette: {
      primary: {
        main: '#3AB795',
      },
      secondary: {
        main: '#ffffff',
      },
    },
  }),
);

theme.components = {
  MuiSvgIcon: {
    defaultProps: {
      color: 'primary',
    },
  },
};

const handleResponseError = async (error) => {
  try {
    const errorMessage =
      error.response?.data?.message ?? error.response?.data?.msg ?? null;
    const code =
      error.response?.data?.code ??
      error.response?.error_code ??
      error.response?.data?.error_code ??
      null;
    const context = error.response?.data?.context ?? null;
    // INFO: Narrower than skipToast, which would also suppress flat errors and
    // the success toast. Matches whatever the form will render inline, so it
    // has to follow the same rules as parseValidationErrors.
    const isInlineValidation =
      error.config?.skipValidationToast &&
      (code === 'VALIDATION_FAILED' || isValidationError(error.response?.data));
    if (!errorMessage && !code) {
      console.error('Error without message:', error);
    } else if (!error.config?.skipToast && !isInlineValidation) {
      const displayMessage = code
        ? i18n.t(`codes.${code}`, {
            ...(context && typeof context === 'object' ? context : {}),
            defaultValue: errorMessage,
          })
        : errorMessage;

      toast.error(displayMessage, {
        toastId: displayMessage,
        position: 'bottom-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }

    if (isPathOpen(window.location.pathname)) {
      return Promise.reject(error);
    }

    const role = error.response?.data?.redirect;
    if (error.response?.status === 401) {
      if (datadogRumEnabled) {
        datadogRum.clearUser();
      }
      window.location.href = `/user/${
        role === 'admin' ? 'login' : 'facilitylogin'
      }?redirect=${window.location.pathname}`;
    }

    return Promise.reject(error);
  } catch (err) {
    console.error('Unexpected error in handleResponseError:', err);
  }
  return Promise.reject(error);
};

const reqInterceptor = axios.interceptors.request.use(
  (config) => {
    if (window.location.pathname.includes('/admin')) {
      if (!config.params) {
        config.params = {};
      }
      config.params.adminUiType = 'admin';
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const resInterceptor = axios.interceptors.response.use(
  (response) => {
    const successMessage = response.data?.message ?? response.data?.msg;
    const code = response.data?.code ?? null;

    if ((successMessage || code) && !response.config?.skipToast) {
      const displayMessage = code
        ? i18n.t(`codes.${code}`, { defaultValue: successMessage })
        : successMessage;

      toast.success(displayMessage, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    }
    return response;
  },
  async (error) => {
    if (
      error.response?.data instanceof Blob &&
      error.response.data.type === 'application/json'
    ) {
      try {
        const text = await error.response.data.text();
        const jsonError = JSON.parse(text);
        error.response.data = jsonError;
      } catch (parseError) {
        console.error('Failed to parse blob error:', parseError);
      }
    }
    return handleResponseError(error);
  },
);

// Eject interceptors on HMR dispose so stale/duplicate handlers don't linger in dev.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    axios.interceptors.request.eject(reqInterceptor);
    axios.interceptors.response.eject(resInterceptor);
  });
}

const App = () => {
  useNavigate();

  const ping = async () => {
    const appState = JSON.parse(localStorage.getItem('appState'));
    const currentPath = window.location.pathname;

    if (appState?.isLoggedIn && !isPathOpen(currentPath)) {
      await axios.get(API_BASE_URL + '/ping');
    }
  };

  useEffect(() => {
    const handleActivity = () => {
      const lastActivity = localStorage.getItem('lastActivity');
      const lastActivityInSecs = moment().diff(moment(lastActivity), 'seconds');

      if (lastActivityInSecs < INACTIVITY_LIMIT - INACTIVITY_POPUP_DURATION) {
        localStorage.setItem('lastActivity', new Date());
      }
    };

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);

    const intervalId = setInterval(() => {
      const lastActivity = localStorage.getItem('lastActivity');
      const lastActivityInSecs = moment().diff(moment(lastActivity), 'seconds');

      if (lastActivityInSecs <= 10) {
        ping();
      }
    }, 10000);

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      axios.interceptors.request.eject(reqInterceptor);
      axios.interceptors.response.eject(resInterceptor);
      clearInterval(intervalId);
    };
  }, []);

  return (
    <>
      <InactivityModal onPing={ping} />
      <Routes>
        <Route path='/' element={<Redirect to='/user/facilitylogin' />} />
        <Route
          path='admin'
          element={<Layout routes={routes} uiRole={'admin'} />}>
          {routes
            .filter((prop) => prop.layout === '/admin')
            .map((prop, key) => (
              <Route
                path={prop.path.replace('/', '')}
                element={<prop.component />}
                key={key}
              />
            ))}
        </Route>

        <Route
          path='facility'
          element={<Layout routes={facilityRoutes} uiRole={'facility'} />}>
          {facilityRoutes
            .filter((prop) => prop.layout === '/facility')
            .map((prop, key) => (
              <Route
                path={prop.path.replace('/', '')}
                element={<prop.component />}
                key={key}
              />
            ))}
        </Route>

        <Route path='user' element={<User />}>
          {routes
            .filter((prop) => prop.layout === '/user')
            .map((prop, key) => (
              <Route
                path={prop.path.replace('/', '')}
                element={<prop.component />}
                key={key}
              />
            ))}
        </Route>

        <Route
          path='/client/clientdetail/:id'
          element={<Redirect to={`${CLAIM_CLIENT_URL}/payments/:id`} />}
        />
        <Route path='/claim' element={<Redirect to={CLAIM_CLIENT_URL} />} />
      </Routes>
      <NeedHelp />
      <ToastContainer
        position='bottom-right'
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='light'
        transition={Bounce}
      />
    </>
  );
};

ReactDOM.createRoot(document.getElementById('root')).render(
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={theme}>
      <PostHogProvider client={posthog}>
        <ConfirmationProvider>
          <Router>
            <App />
          </Router>
        </ConfirmationProvider>
      </PostHogProvider>
    </ThemeProvider>
  </StyledEngineProvider>,
);
